import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, Facebook, Instagram, Lock, Handshake } from "lucide-react";
import { NAV_ITEMS, LOGO, LOGO_EXPRESSO } from "../../lib/site";

function LicencieButton({ className = "" }) {
  return (
    <Link
      to="/espace"
      data-testid="nav-espace-licencie"
      title="Espace Licencié — connexion ou inscription"
      className={`inline-flex items-center gap-2 border border-white/25 bg-white/5 text-white font-heading uppercase tracking-wider text-sm px-5 py-2.5 hover:bg-rouge hover:border-rouge transition-[background-color,border-color] duration-300 ${className}`}
    >
      <Lock size={14} />
      Espace Licencié
    </Link>
  );
}

function PartnerButton({ onClick }) {
  return (
    <Link
      to="/partenaires"
      onClick={onClick}
      data-testid="nav-devenir-partenaire"
      className="inline-flex items-center gap-2 border border-white/25 bg-white/5 text-white font-heading uppercase tracking-wider text-sm px-5 py-2.5 hover:bg-rouge hover:border-rouge transition-[background-color,border-color] duration-300"
    >
      <Handshake size={14} />
      Devenir Partenaire
    </Link>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  const isActive = (to) => to === "/" ? location.pathname === "/" : location.pathname.startsWith(to.split("#")[0]);

  return (
    <>
      <nav
        data-testid="main-nav"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-500 ${
          scrolled ? "bg-navy/95 shadow-2xl shadow-navy/40" : "bg-navy/85"
        } backdrop-blur-xl border-b border-white/10`}
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
              <img
                src={LOGO}
                alt="Club Cycliste Le Boulou — logo officiel"
                className="h-12 w-auto bg-white px-2 py-1 transition-transform duration-300 group-hover:scale-[1.03]"
              />
            </Link>

            <div className="hidden xl:flex items-center gap-1">
              {NAV_ITEMS.map((item) =>
                item.children ? (
                  <div key={item.label} className="relative group">
                    <Link
                      to={item.to}
                      data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                      className={`flex items-center gap-1 px-3 py-2 font-heading uppercase tracking-wider text-[13px] transition-colors duration-300 ${
                        isActive(item.to) ? "text-rouge" : "text-white/80 group-hover:text-white"
                      }`}
                    >
                      {item.label}
                      <ChevronDown size={12} className="transition-transform duration-300 group-hover:rotate-180" />
                    </Link>
                    <div className="absolute left-0 top-full pt-2 opacity-0 invisible translate-y-2 transition-[opacity,transform,visibility] duration-300 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0">
                      <div className="bg-navy2 border border-white/10 shadow-2xl min-w-[220px] py-2">
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.to}
                            className="block px-5 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/5 hover:pl-6 transition-[color,background-color,padding] duration-300"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    key={item.label}
                    to={item.to}
                    data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    className={`px-3 py-2 font-heading uppercase tracking-wider text-[13px] transition-colors duration-300 ${
                      isActive(item.to) ? "text-rouge" : "text-white/80 hover:text-white"
                    }`}
                  >
                    {item.label}
                  </Link>
                )
              )}
              <div className="ml-3 flex items-center gap-3">
                <PartnerButton />
                <LicencieButton />
              </div>
              <img
                src={LOGO_EXPRESSO}
                alt="Expresso Racing — logo partenaire"
                data-testid="nav-logo-expresso"
                className="ml-4 h-12 w-12 rounded-full ring-2 ring-white/20 transition-transform duration-300 hover:scale-105"
              />
            </div>

            <div className="xl:hidden flex items-center gap-4">
              <img
                src={LOGO_EXPRESSO}
                alt="Expresso Racing — logo partenaire"
                data-testid="nav-logo-expresso-mobile"
                className="h-10 w-10 rounded-full ring-2 ring-white/20"
              />
              <button
                data-testid="nav-mobile-toggle"
                onClick={() => setOpen(!open)}
                className="text-white p-2"
                aria-label="Ouvrir le menu"
              >
                {open ? <X size={26} /> : <Menu size={26} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-navy overflow-y-auto pt-24 pb-10"
          >
            <div className="px-6 flex flex-col gap-1">
              {NAV_ITEMS.map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.05, duration: 0.4 }}
                >
                  <Link
                    to={item.to}
                    className={`block font-heading uppercase font-semibold tracking-tight text-3xl py-2.5 ${
                      isActive(item.to) ? "text-rouge" : "text-white"
                    }`}
                  >
                    {item.label}
                  </Link>
                  {item.children && (
                    <div className="pl-4 pb-2 flex flex-col gap-1">
                      {item.children.map((child) => (
                        <Link key={child.label} to={child.to} className="text-white/60 text-sm py-1">
                          — {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55 }}
                className="mt-8 flex flex-wrap items-center gap-3"
              >
                <PartnerButton onClick={() => setOpen(false)} />
                <LicencieButton />
              </motion.div>
              <div className="mt-10 flex gap-4 text-white/60">
                <a href="#" aria-label="Facebook" data-testid="mobile-social-facebook" className="hover:text-rouge transition-colors"><Facebook size={20} /></a>
                <a href="#" aria-label="Instagram" data-testid="mobile-social-instagram" className="hover:text-rouge transition-colors"><Instagram size={20} /></a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
